import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  and,
  collection,
  getCountFromServer,
  getDocs,
  or,
  orderBy,
  query,
  where,
} from 'firebase/firestore';

import { ICollection, ICollectionWithStoryCounts } from 'types/collection';
import { ISanitizedCompany } from 'types/company';

import { clientCollectionConverter } from 'services/api/firestore/converters';

import { db } from 'clients/firebase';

import { useUser } from 'hooks/auth/user';

export const useFetchCollections = (
  company: ISanitizedCompany,
  options: null | {
    orderBy: [string, 'asc' | 'desc'];
    isPublished: boolean;
    showInFeed?: boolean;
    userId?: string;
    inTeam?: boolean;
  },
) => {
  const { user } = useUser();

  const queryConstraints = useMemo(() => {
    if (!options) {
      return null;
    }

    const queryConstraints = [where('meta.companyId', '==', company.id)];

    if (options.isPublished) {
      queryConstraints.push(where('meta.publishedAt', '!=', null));
    }

    if (options.showInFeed) {
      queryConstraints.push(where('meta.showInFeed', '==', true));
    }

    if (options.userId) {
      queryConstraints.push(where('meta.userId', '==', options.userId));
    }

    if (options.inTeam && (!user || !user.team || user.team.length === 0)) {
      queryConstraints.push(where('meta.team', '==', []));
    }

    return queryConstraints;
  }, [company, options, user]);

  const compositeFilter = useMemo(() => {
    let compositeFilter;
    if (options?.inTeam && user && user.team && user.team.length > 0) {
      compositeFilter = or(
        where('meta.team', 'array-contains-any', user.team),
        where('meta.team', '==', []),
      );
    }

    return compositeFilter;
  }, [options?.inTeam, user]);

  const [collectionsWithStoryCounts, setCollectionsWithStoryCounts] = useState<
    ICollectionWithStoryCounts[] | null
  >(null);

  useEffect(() => {
    (async () => {
      if (!options || !queryConstraints) {
        return;
      }

      try {
        let baseQuery;
        if (compositeFilter) {
          baseQuery = query(
            collection(db, 'Collections'),
            and(...queryConstraints, compositeFilter),
            orderBy(...options.orderBy),
          );
        } else {
          baseQuery = query(
            collection(db, 'Collections'),
            ...queryConstraints,
            orderBy(...options.orderBy),
          );
        }

        const { docs: collectionDocs } = await getDocs(
          query(baseQuery).withConverter(clientCollectionConverter),
        );

        const collections = collectionDocs.map(
          doc => doc.data() as ICollection,
        );

        const collectionsWithStoryCounts: ICollectionWithStoryCounts[] = [];

        for (const coll of collections) {
          const storiesRef = collection(db, 'Stories');
          const totalCountQuery = query(
            storiesRef,
            where('meta.collectionId', '==', coll.id),
            where('meta.companyId', '==', company.id),
          );
          const draftCountQuery = query(
            storiesRef,
            where('meta.collectionId', '==', coll.id),
            where('meta.companyId', '==', company.id),
            where('meta.publishedAt', '==', null),
          );
          const publishedCountQuery = query(
            storiesRef,
            where('meta.collectionId', '==', coll.id),
            where('meta.companyId', '==', company.id),
            where('meta.publishedAt', '!=', null),
          );

          const totalStoryCount = (
            await getCountFromServer(totalCountQuery)
          ).data().count;
          const draftStoryCount = (
            await getCountFromServer(draftCountQuery)
          ).data().count;
          const publishedStoryCount = (
            await getCountFromServer(publishedCountQuery)
          ).data().count;
          collectionsWithStoryCounts.push({
            collection: coll,
            totalStoryCount,
            draftStoryCount,
            publishedStoryCount,
          });
        }

        setCollectionsWithStoryCounts(collectionsWithStoryCounts);
      } catch (error) {
        console.error('Error fetching collections', error);
        // Settle out of the null loading state so consumers stop showing
        // skeletons.
        setCollectionsWithStoryCounts([]);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryConstraints]);

  const patchCollection = useCallback((updated: ICollection) => {
    setCollectionsWithStoryCounts(prev =>
      prev?.map(c =>
        c.collection.id === updated.id ? { ...c, collection: updated } : c,
      ) ?? prev,
    );
  }, []);

  return {
    collectionsWithStoryCounts,
    patchCollection,
  };
};
